import type { AnimationsBehavior } from '@wix/thunderbolt-becky-root'
import { HeaderSectionCss, HeaderSectionDefinition } from './HeaderSection'
import { withCompInfo } from '@wix/editor-elements-integrations'
import { css as sectionCss, designCss as sectionDesignCss } from '../Section/Section.mapper'

const MOVE_ANIMATION_NAME = 'HeaderMoveCustom'
const FADE_ANIMATION_NAME = 'HeaderFadeOutCustom'

const defaultDurationMapping: Record<string, string> = {
	[FADE_ANIMATION_NAME]: '0.5s',
	[MOVE_ANIMATION_NAME]: '0.25s',
}

const getDefaultDurationForAnimation = (animationName: string | undefined) => {
	return (animationName && defaultDurationMapping[animationName]) || '0.25s'
}

const easeMapping: Record<string, string> = {
	'Linear.easeNone': 'linear',
	'Sine.easeIn': 'cubic-bezier(0.12, 0, 0.39, 0)',
	'Sine.easeOut': 'cubic-bezier(0.61, 1, 0.88, 1)',
	'Sine.easeInOut': 'cubic-bezier(0.37, 0, 0.63, 1)',
	'Power1.easeIn': 'cubic-bezier(0.11, 0, 0.5, 0)',
	'Power1.easeOut': 'cubic-bezier(0.5, 1, 0.89, 1)',
	'Power1.easeInOut': 'cubic-bezier(0.45, 0, 0.55, 1)',
	'Power2.easeIn': 'cubic-bezier(0.32, 0, 0.67, 0)',
	'Power2.easeOut': 'cubic-bezier(0.33, 1, 0.68, 1)',
	'Power2.easeInOut': 'cubic-bezier(0.65, 0, 0.35, 1)',
	'Power3.easeIn': 'cubic-bezier(0.5, 0, 0.75, 0)',
	'Power3.easeOut': 'cubic-bezier(0.25, 1, 0.5, 1)',
	'Power3.easeInOut': 'cubic-bezier(0.76, 0, 0.24, 1)',
}

const getCssEaseFromGsapEase = (ease: string, defaultEase: string = 'linear') => {
	return easeMapping[ease] || defaultEase
}

const getHeaderAnimation = (animations: Array<AnimationsBehavior>): AnimationsBehavior | undefined => {
	const headerAnimations = animations
		.filter((item) => {
			return item.name === 'AnimateAfterScroll'
		})
		.filter((item) => {
			const animationName = item.params?.animations[0].name
			return animationName === FADE_ANIMATION_NAME || animationName === MOVE_ANIMATION_NAME
		})
		.map((item) => {
			return item.params?.animations[0] as AnimationsBehavior
		})
	return headerAnimations[0]
}

const getMoveTransform = (toObject: { x?: string; y?: string }) => {
	const isX = !!toObject.x
	return isX ? `translateX(${toObject.x})` : `translateY(${toObject.y})`
}

const sectionDeps = Object.keys(sectionCss.deps)

export const css = withCompInfo<HeaderSectionCss, HeaderSectionDefinition>()(
	// @ts-ignore - remove once animations is added to CompInfo
	['skin', 'animations', ...sectionDeps],
	(deps, carmiData) => {
		// @ts-ignore - remove once animations is added to CompInfo
		const { skin, animations } = deps
		const sectionCssVars = sectionCss.resolver(deps, carmiData)

		const headerAnimation = getHeaderAnimation(animations as Array<AnimationsBehavior>)
		const isAnimated = !!headerAnimation
		const animationName = headerAnimation?.name
		const hasScrollColorChange = skin === 'RectangleAreaAfterScroll'
		const isFadeAnimation = animationName === FADE_ANIMATION_NAME

		const animatedPropCss = isAnimated
			? isFadeAnimation
				? {
						'--scrolled-opacity': '0',
						'--transition-property': hasScrollColorChange ? 'background-color,opacity' : 'opacity',
				  }
				: {
						'--scrolled-transform': getMoveTransform(headerAnimation.params?.to),
						'--transition-property': hasScrollColorChange ? 'background-color,transform' : 'transform',
				  }
			: hasScrollColorChange
			? {
					'--transition-property': 'background-color',
			  }
			: {}
		const delay = headerAnimation?.delay
		const duration = headerAnimation?.duration

		const animationDelay = delay ? `${delay}s` : '0s'
		const animationDuration = duration ? `${duration}s` : getDefaultDurationForAnimation(animationName)
		const defaultTimingFunction = isFadeAnimation
			? getCssEaseFromGsapEase('Power3.easeIn')
			: getCssEaseFromGsapEase('Linear.easeNone')

		const animationTimingFunction = getCssEaseFromGsapEase(
			headerAnimation?.params?.ease || '',
			defaultTimingFunction
		)
		const colorChangeDelay = '0s'
		const colorChangeTransitionDuration = '0.3s'
		const colorChangeTimingFunction = 'ease'

		const baseAnimationCss = isAnimated
			? {
					'--transition-delay': hasScrollColorChange
						? `${colorChangeDelay},${animationDelay}`
						: animationDelay,
					'--transition-duration': hasScrollColorChange
						? `${colorChangeTransitionDuration},${animationDuration}`
						: animationDuration,
					'--transition-timing-function': hasScrollColorChange
						? `${colorChangeTimingFunction},${animationTimingFunction}`
						: animationTimingFunction,
			  }
			: hasScrollColorChange
			? {
					'--transition-delay': colorChangeDelay,
					'--transition-duration': colorChangeTransitionDuration,
					'--transition-timing-function': colorChangeTimingFunction,
			  }
			: {}

		return {
			...sectionCssVars,
			...baseAnimationCss,
			...animatedPropCss,
		}
	}
)

export const designCss = sectionDesignCss
